import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
// CJS default export: use `import { create as createDropin }` if `create` is ever undefined.
import dropin from 'braintree-web-drop-in';
import 'braintree-web-drop-in/dropin.css';
import { customToast } from '@/utils';
import { coreDomain } from '@/redux/constant';

const DEFAULT_CARD_NETWORKS = ['visa', 'mastercard'];

const BillingModal = ({
  onPaymentMethodAvailable,
  onPaymentMethodNotAvailable,
  onBraintreeInstance,
  onPaymentPayload,
  amount = '9.00',
  currency = 'USD',
  countryCode = 'US',
  merchantName = 'ILOLA',
  googlePayMerchantId = 'BCR2DN4T6PYZDGIQ',
  supportedCardNetworks = DEFAULT_CARD_NETWORKS
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const containerRef = useRef(null);
  const instanceRef = useRef(null);
  const isMountedRef = useRef(true);

  const onBraintreeInstanceRef = useRef(onBraintreeInstance);
  const onPaymentMethodAvailableRef = useRef(onPaymentMethodAvailable);
  const onPaymentMethodNotAvailableRef = useRef(onPaymentMethodNotAvailable);
  const onPaymentPayloadRef = useRef(onPaymentPayload);
  const lastPayloadNonceRef = useRef(null);

  useEffect(() => {
    onBraintreeInstanceRef.current = onBraintreeInstance;
    onPaymentMethodAvailableRef.current = onPaymentMethodAvailable;
    onPaymentMethodNotAvailableRef.current = onPaymentMethodNotAvailable;
    onPaymentPayloadRef.current = onPaymentPayload;
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const cardNetworksKey = supportedCardNetworks.join(',');

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const init = async () => {
      try {
        setIsLoading(true);

        if (instanceRef.current) {
          try {
            await instanceRef.current.teardown();
          } catch (err) {
            console.warn('Previous Braintree teardown failed:', err);
          }
          instanceRef.current = null;
        }

        const response = await fetch(
          `${coreDomain}/ChargeBeePayments/GetClientToken`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to get client token: ${response.status}`);
        }

        const data = await response.json();
        if (!data?.result) {
          throw new Error('Invalid client token response');
        }

        if (cancelled || controller.signal.aborted || !isMountedRef.current) {
          return;
        }
        if (!containerRef.current) {
          throw new Error('Drop-in container is not mounted');
        }

        const create = dropin.create;
        if (typeof create !== 'function') {
          throw new Error('Braintree Drop-in create() is not available');
        }

        const instance = await create({
          authorization: data.result,
          container: containerRef.current,
          paypal: { flow: 'vault', amount, currency },
          googlePay: {
            googlePayVersion: 2,
            merchantInfo: {
              merchantName,
              merchantId: googlePayMerchantId
            },
            transactionInfo: {
              totalPriceStatus: 'FINAL',
              totalPrice: amount,
              currencyCode: currency
            },
            allowedPaymentMethods: [
              {
                type: 'CARD',
                parameters: {
                  billingAddressRequired: true,
                  billingAddressParameters: { format: 'FULL' }
                }
              }
            ]
          },
          applePay: {
            displayName: merchantName,
            paymentRequest: {
              total: { label: merchantName, amount },
              countryCode,
              currencyCode: currency,
              supportedNetworks: supportedCardNetworks
            }
          },
          venmo: {
            allowNewBrowserTab: true,
            allowDesktop: true,
            allowDesktopWebLogin: true,
            mobileWebFallBack: true
          },
          card: { overrides: { fields: { cvv: { required: true } } } }
        });

        if (cancelled || controller.signal.aborted || !isMountedRef.current) {
          try {
            await instance.teardown();
          } catch (err) {
            console.log('Discarded Braintree instance teardown failed:', err);
          }
          return;
        }

        instanceRef.current = instance;

        instance.on('paymentMethodRequestable', event => {
          if (!isMountedRef.current) return;
          onPaymentMethodAvailableRef.current?.();

          if (event && event.paymentMethodIsSelected === false) {
            return;
          }

          try {
            instance.requestPaymentMethod((err, payload) => {
              if (err) {
                console.error('[Braintree] requestPaymentMethod error:', err);
                return;
              }
              if (!payload) {
                console.warn('[Braintree] requestPaymentMethod returned empty payload');
                return;
              }

              const nonce = payload?.nonce || null;
              if (nonce && lastPayloadNonceRef.current === nonce) return;
              lastPayloadNonceRef.current = nonce;
              onPaymentPayloadRef.current?.(payload);
            });
          } catch (e) {
            console.log('Failed to prefetch payment payload:', e);
          }
        });
        instance.on('noPaymentMethodRequestable', () => {
          if (!isMountedRef.current) return;
          onPaymentMethodNotAvailableRef.current?.();
        });
        instance.on('error', err => {
          console.error('Braintree Drop-in error:', err);
          customToast.error(err?.message || 'Payment widget error. Please try again.');
        });

        onBraintreeInstanceRef.current?.(instance);
        setError(null);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Error initializing Braintree:', err);
        if (cancelled || !isMountedRef.current) return;
        setError('Failed to initialize the payment form. Please try again.');
      } finally {
        if (!cancelled && isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    init();

    const containerAtMount = containerRef.current;

    return () => {
      cancelled = true;
      controller.abort();

      const dying = instanceRef.current;
      instanceRef.current = null;

      const clearContainer = () => {
        if (containerAtMount) {
          containerAtMount.innerHTML = '';
        }
      };

      if (dying) {
        Promise.resolve()
          .then(() => dying.teardown())
          .catch(err => console.warn('Braintree teardown failed:', err))
          .finally(clearContainer);
      } else {
        clearContainer();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    retryKey,
    amount,
    currency,
    countryCode,
    merchantName,
    googlePayMerchantId,
    cardNetworksKey
  ]);

  const handleRetry = () => {
    setError(null);
    setRetryKey(k => k + 1);
  };

  const showDropIn = !error;
  const showLoadingOverlay = isLoading && !error;

  return (
    <Box>
      {error && (
        <Box
          role='alert'
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}
        >
          <Typography variant='body2' color='error'>
            {error}
          </Typography>
          <Button
            onClick={handleRetry}
            variant='outlined'
            size='small'
            sx={{ alignSelf: 'flex-start' }}
          >
            Retry
          </Button>
        </Box>
      )}

      {showDropIn && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: 240
          }}
        >
          {showLoadingOverlay && (
            <Box
              role='status'
              aria-live='polite'
              aria-busy
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 3,
                borderRadius: 1,
                bgcolor: theme => theme.palette.action.hover
              }}
            >
              <CircularProgress size={22} thickness={2} />
              <Typography
                component='h3'
                variant='subtitle1'
                fontWeight={600}
                textAlign='center'
                gutterBottom
              >
                Loading secure payment
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                textAlign='center'
                sx={{ maxWidth: 320, mb: 2 }}
              >
                Please wait while we connect to the payment provider. This may take a few
                seconds on a slow connection.
              </Typography>
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Skeleton variant='rounded' height={10} sx={{ mb: 1.25 }} />
                <Skeleton variant='rounded' height={10} width='85%' sx={{ mb: 1.25 }} />
                <Skeleton variant='rounded' height={44} />
              </Box>
            </Box>
          )}

          <Box
            ref={containerRef}
            sx={{
              display: 'block',
              minHeight: 240,
              opacity: isLoading ? 0 : 1,
              // pointerEvents: isLoading ? 'none' : 'auto',
              transition: 'opacity 0.2s ease-out'
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default BillingModal;
